import type React from 'react';
import { createContext, useContext, useMemo, useRef, useState } from 'react';

import OT from '@opentok/client';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';

interface MediaConstraints {
  audio: boolean;
  video?: boolean;
}

interface IPermissionContext {
  mediaStatus: boolean;
  isPermited: { audio: boolean; video: boolean };
  isWaiting: boolean;
  streamRef: React.MutableRefObject<MediaStream | null>;
  onClickPermission: (value?: boolean) => Promise<void>;
  checkPermissions: () => Promise<void>;
}

const PermissionContext = createContext<IPermissionContext>({} as IPermissionContext);

const PermissionContextProvider: React.FC<React.ReactNode> = ({ children }) => {
  const [mediaStatus, setMediaStatus] = useState<boolean>(false);
  const [isWaiting, setWaiting] = useState<boolean>(false);
  const [isPermited, setPermited] = useState<{ audio: boolean; video: boolean }>({
    audio: false,
    video: false
  });
  const streamRef = useRef<MediaStream | null>(null);
  const { log } = useActivityLogger();

  const getAvailableDevices = async (): Promise<{ video: OT.Device[]; audio: OT.Device[] }> =>
    await new Promise((resolve) => {
      OT.getDevices((error, devices) => {
        if (error) {
          resolve({ audio: [], video: [] });
          return;
        }
        resolve({
          audio: devices?.filter((element) => element.kind === 'audioInput' && !!element.deviceId) || [],
          video: devices?.filter((element) => element.kind === 'videoInput' && !!element.deviceId) || []
        });
      });
    });

  const checkPermissions = async () => {
    if (!navigator.permissions.query) {
      console.warn('Permissions API not supported.');
      const devices = await getAvailableDevices();
      setMediaStatus(devices.audio.length > 0);
      setPermited({
        audio: devices.audio.length > 0,
        video: devices.video.length > 0
      });
      return;
    }

    try {
      // @ts-ignore
      const micPermission = await navigator.permissions.query({ name: 'microphone' });
      // @ts-ignore
      const cameraPermission = await navigator.permissions.query({ name: 'camera' });

      const micGranted = micPermission.state === 'granted';
      const cameraGranted = cameraPermission.state === 'granted';

      setMediaStatus(micGranted || micPermission.state !== 'prompt');
      setPermited({
        audio: micGranted,
        video: cameraGranted
      });
      console.warn(`Permitted - Audio: ${micGranted} Video: ${cameraGranted}`);
    } catch (error) {
      console.warn('Error while checking permissions:', error);

      void log({
        action: Action.ERROR,
        description: `Error while checking permissions, error message: ${(error as Error).message}`,
        index: Index.ACTIVITY,
        label: 'ERROR'
      });

      setMediaStatus(false);
      setPermited({
        audio: false,
        video: false
      });
    }
  };

  const onClickPermission = async (isAudioOnly?: boolean) => {
    setWaiting(true);

    try {
      const mediaOptions: MediaConstraints = { audio: true };
      if (!isAudioOnly) {
        mediaOptions.video = true;
      }

      const stream = await navigator.mediaDevices.getUserMedia(mediaOptions);
      streamRef.current = stream;
      checkPermissions();
    } catch (error) {
      console.warn('Permission denied:', error);
      const errorType = error as { name: string };
      if (errorType.name === 'NotAllowedError' || errorType.name === 'NotFoundError') {
        console.warn(`Access denied to camera and microphone: ${errorType.name}`);
        setPermited({
          audio: false,
          video: false
        });
      }

      setMediaStatus(true);
    } finally {
      setWaiting(false);
      void log({
        action: Action.PERMISSION_REQUESTED,
        description: 'Audio Video Permission Requested.',
        index: Index.ACTIVITY,
        label: 'PERMISSION_REQUESTED'
      });
    }
  };

  const initialValue = useMemo(
    () => ({
      checkPermissions,
      isPermited,
      isWaiting,
      mediaStatus,
      onClickPermission,
      streamRef
    }),
    [mediaStatus, isPermited.audio, isPermited.video, isWaiting, streamRef, onClickPermission, checkPermissions]
  );

  return <PermissionContext.Provider value={initialValue}>{children}</PermissionContext.Provider>;
};

export const usePermission = (): IPermissionContext => {
  const { mediaStatus, isPermited, isWaiting, streamRef, onClickPermission, checkPermissions } =
    useContext(PermissionContext);
  return {
    checkPermissions,
    isPermited,
    isWaiting,
    mediaStatus,
    onClickPermission,
    streamRef
  };
};

export default PermissionContextProvider;
